import React, { useState, useEffect } from 'react';
import {
    getInactiveProviders,
    activateProvider,
    listAllProviders,
    listServiceProviders,
    listProductProviders,
    getProviderComp,
    deleteProvider
} from "../../services/providersApi";
import Spinner from "../Spinner/Spinner";
import {fetchImages, getPhoto} from "../../storageService";
import { Eye, Check, Copy, Plus, FileX } from "lucide-react";
import AutocompleteSearch from "../Home/AutocompleteSearch";
import './ControlPanel.css';
import { toast } from "react-toastify";
import ProviderData from './ProviderData';
import {
    createServiceProvider,
    createProductsProvider
} from "../../services/controlPanelApi";
import CreateProviderModal from './CreateProviderModal';

const InactiveProvidersList = () => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [images, setImages] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [allSearchResults, setAllSearchResults] = useState({});
    const [filteredResults, setFilteredResults] = useState({});
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [showAllProviders, setShowAllProviders] = useState(false);
    const [providerFilter, setProviderFilter] = useState('all'); // 'all', 'service', 'products'
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [providerToDuplicate, setProviderToDuplicate] = useState(null);
    const [showCopyModal, setShowCopyModal] = useState(false);

    // Cargo proveedores según el modo seleccionado (inactivos o todos)
    useEffect(() => {
        const fetchProviders = async () => {
            setLoading(true);
            try {
                let data;
                if (showAllProviders) {
                    // Determinar qué función usar según el filtro
                    switch (providerFilter) {
                        case 'service':
                            data = await listServiceProviders();
                            break;
                        case 'products':
                            data = await listProductProviders();
                            break;
                        default:
                            data = await listAllProviders();
                            break;
                    }

                    // Asegurar que siempre trabajemos con un array de usernames
                    if (data && Array.isArray(data)) {
                        data = data.map(provider => {
                            // Si es un objeto, extraer solo el username
                            if (typeof provider === 'object' && provider !== null) {
                                return provider.username;
                            }
                            // Si es un string, devolverlo tal cual
                            return provider;
                        });
                    }
                } else {
                    data = await getInactiveProviders();
                    // Asegurar que los datos de proveedores inactivos sean objetos válidos
                    if (data && Array.isArray(data)) {
                        data = data.filter(provider =>
                            typeof provider === 'object' && provider !== null && provider.username
                        );
                    }
                }
                setProviders(data || []);
            } catch (err) {
                console.error(`Error fetching ${showAllProviders ? 'active' : 'inactive'} providers:`, err);
                toast.error(`No se pudieron cargar los proveedores ${showAllProviders ? 'activos' : 'inactivos'}.`);
                setProviders([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProviders();
    }, [showAllProviders, providerFilter]);

    // Cargo todos los usernames para el autocomplete
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const list = await listAllProviders(); // devuelve array de objetos o strings
                const map = {};

                if (list && Array.isArray(list)) {
                    list.forEach(item => {
                        // Si el elemento es un objeto, extraer el username
                        if (typeof item === 'object' && item !== null) {
                            map[item.username] = item.username;
                        } else if (typeof item === 'string') {
                            map[item] = item;
                        }
                    });
                }

                setAllSearchResults(map);
            } catch (err) {
                console.error("Error fetching all providers:", err);
            }
        };
        fetchAll();
    }, []);

    // Filtrado de resultados conforme a query
    useEffect(() => {
        if (searchQuery) {
            const filt = Object.entries(allSearchResults).reduce((acc, [key, val]) => {
                if (key.toLowerCase().includes(searchQuery.toLowerCase())) {
                    acc[key] = val;
                }
                return acc;
            }, {});
            setFilteredResults(filt);
        } else if (isSearchFocused) {
            setFilteredResults(allSearchResults);
        } else {
            setFilteredResults({});
        }
    }, [searchQuery, allSearchResults, isSearchFocused]);

    const handleSearchQueryChange = e => setSearchQuery(e.target.value);
    const handleSearchFocus = () => {
        setIsSearchFocused(true);
        setFilteredResults(allSearchResults);
    };
    const handleSearchBlur = () => {
        setTimeout(() => {
            setIsSearchFocused(false);
            if (!searchQuery) setFilteredResults({});
        }, 200);
    };
    const handleKeyPress = e => {
        if (e.key === "Enter") e.preventDefault();
    };

    const getAndSetImages = async (provider) => {
        const imgs = await fetchImages(provider.photo, provider.workPhotos).catch(() => ({}));
        imgs.documentPhoto = await getPhoto(provider.documentPhoto);
        setImages(imgs);
    }

    // Al seleccionar un resultado, traigo el proveedor
    const handleResultSelect = async (_label, providerId) => {
        setLoading(true);
        try {
            const prov = await getProviderComp(providerId);
            setSelectedProvider(prov);
            await getAndSetImages(prov);
            toast.success(`Proveedor "${prov.name}" cargado`);
        } catch (err) {
            console.error(err);
            toast.error("No se pudo obtener el proveedor.");
        } finally {
            setLoading(false);
            setSearchQuery("");
            setFilteredResults({});
            setIsSearchFocused(false);
        }
    };

    // Maneja la acción del proveedor desde el componente ProviderDetail
    const handleProviderAction = (action, providerId, updatedData) => {
        switch (action) {
            case 'activate':
                setSelectedProvider(prev => prev && { ...prev, active: true });
                setProviders(prev => prev.filter(p => p.username !== providerId));
                break;
            case 'deactivate':
                setSelectedProvider(prev => prev && { ...prev, active: false });
                // lo vuelvo a meter en la tabla de inactivos si no estaba ya
                if (!providers.some(p => p.username === providerId) && !showAllProviders) {
                    setProviders(prev => [
                        ...prev,
                        { ...selectedProvider, active: false }
                    ]);
                }
                break;
            case 'delete':
                setSelectedProvider(null);
                setProviders(prev => prev.filter(p => p.username !== providerId));
                break;
            case 'update':
                setSelectedProvider(updatedData);
                // Si está en la lista actual, actualizo también allí
                if (providers.some(p => p.username === providerId)) {
                    setProviders(prev =>
                        prev.map(p => p.username === providerId ? { ...p, ...updatedData } : p)
                    );
                }
                break;
            case 'duplicate':
                // Manejar la acción de duplicar desde el modal de detalle
                handleDuplicateProvider(updatedData);
                break;
            default:
                break;
        }
    };

    // Activa un proveedor directamente desde la tabla
    const handleActivateProvider = async (id) => {
        try {
            setLoading(true);
            await activateProvider(id);
            toast.success("Proveedor activado");
            setProviders(prev => prev.filter(p => p.username !== id));
        } catch (err) {
            console.error(err);
            // El backend devuelve el motivo concreto (localidad/categoría/subcategoría) en el body
            const backendMsg = typeof err?.response?.data === 'string' ? err.response.data : null;
            toast.error(backendMsg || "Error al activar el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Duplicar un proveedor
    const handleDuplicateProvider = async (provider) => {
        setProviderToDuplicate(provider);
        setShowCreateModal(true);
    };

    // Eliminar un proveedor usando deleteProvider de providersApi
    const handleDeleteProvider = async (username, type) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar al proveedor ${username}? Esta acción no se puede deshacer.`)) {
            return;
        }

        try {
            setLoading(true);
            await deleteProvider(username, type);
            toast.success("Proveedor eliminado con éxito");
            setProviders(prev => prev.filter(p => p.username !== username));
        } catch (err) {
            console.error("Error al eliminar proveedor:", err);
            toast.error("No se pudo eliminar el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Crear un nuevo proveedor (en blanco o duplicado)
    const handleCreateProvider = async (providerData) => {
        try {
            setLoading(true);
            const { type } = providerData;

            if (type === 'service') {
                await createServiceProvider(providerData);
            } else {
                await createProductsProvider(providerData);
            }

            toast.success("Proveedor creado con éxito");
            setShowCreateModal(false);
            setProviderToDuplicate(null);

            // Recargar lista si es un proveedor inactivo o si estamos mostrando todos
            if (!providerData.active || showAllProviders) {
                const newData = showAllProviders
                    ? await listAllProviders()
                    : await getInactiveProviders();
                setProviders(newData);
            }
        } catch (err) {
            console.error("Error al crear proveedor:", err);
            toast.error("No se pudo crear el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Toggle entre mostrar inactivos o todos
    const handleToggleProviders = () => {
        setShowAllProviders(prev => !prev);
        setProviderFilter('all'); // Reset filter when toggling
    };

    // Cambiar filtro de tipo de proveedor
    const handleFilterChange = (filterType) => {
        setProviderFilter(filterType);
    };

    // Copiar usernames al clipboard
    const handleCopyUsernames = () => {
        if (providers.length === 0) {
            toast.warn("No hay proveedores para copiar.");
            return;
        }

        const usernames = providers
            .map(provider => typeof provider === 'string' ? provider : provider.username)
            .filter(Boolean)
            .join(', ');

        navigator.clipboard.writeText(usernames).then(() => {
            toast.success("Usernames copiados al portapapeles!");
            setShowCopyModal(false);
        }).catch(() => {
            toast.error("Error al copiar usernames.");
        });
    };

    // Seleccionar un username de la lista cuando está en modo "Todos"
    const handleUsernameSelect = async (username) => {
        await handleResultSelect(null, username);
    };

    return (
        <div className="control-panel-card wide-card providers-management-container">
            {loading && <Spinner />}

            <div className="providers-header">
                <h2 className="providers-title">Gestión de Proveedores</h2>

                <div className="inactive-providers-actions">
                    <div className="inactive-providers-toggle-container">
                        <span className={`inactive-providers-active-option ${!showAllProviders ? "active" : ""}`}>No Activos</span>
                        <label className="inactive-providers-switch">
                            <input
                                type="checkbox"
                                checked={showAllProviders}
                                onChange={handleToggleProviders}
                            />
                            <span className="inactive-providers-slider"></span>
                        </label>
                        <span className={`inactive-providers-active-option ${showAllProviders ? "active" : ""}`}>Todos</span>
                    </div>

                    {showAllProviders && (
                        <div className="inactive-providers-filter-buttons">
                            <button
                                className={`inactive-providers-filter-btn ${providerFilter === 'all' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('all')}
                            >
                                Todos
                            </button>
                            <button
                                className={`inactive-providers-filter-btn ${providerFilter === 'service' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('service')}
                            >
                                Servicios
                            </button>
                            <button
                                className={`inactive-providers-filter-btn ${providerFilter === 'products' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('products')}
                            >
                                Productos
                            </button>
                        </div>
                    )}

                    <div className="inactive-providers-action-buttons">
                        {showAllProviders && providers.length > 0 && (
                            <button
                                className="provider-action-btn inactive-providers-copy-usernames-btn"
                                onClick={() => setShowCopyModal(true)}
                                title="Copiar usernames"
                            >
                                <Copy size={18}/>
                                <span className="provider-action-text">Copiar Usernames</span>
                            </button>
                        )}

                        <button
                            className="provider-action-btn create-provider-btn"
                            onClick={() => setShowCreateModal(true)}
                            title="Crear nuevo proveedor"
                        >
                            <Plus size={18}/>
                            <span className="provider-action-text">Nuevo Proveedor</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="providers-search">
                <h4>Buscador de Proveedores</h4>
                <AutocompleteSearch
                    searchQuery={searchQuery}
                    searchResults={filteredResults}
                    handleSearchQueryChange={handleSearchQueryChange}
                    handleKeyPress={handleKeyPress}
                    handleResultSelect={handleResultSelect}
                    handleSearchFocus={handleSearchFocus}
                    handleSearchBlur={handleSearchBlur}
                />
            </div>

            <h3 className="providers-list-title">
                {showAllProviders ? "Todos los Proveedores" : "Proveedores No Activos"}
            </h3>

            {!providers.length ? (
                <p className="no-providers-message">No hay proveedores {!showAllProviders && "pendientes de activación"}.</p>
            ) : showAllProviders ? (
                // Listado de usernames con estilos modernos y específicos
                <div className="cp-listado-usernames-grid">
                    {providers.map(username => (
                        typeof username === 'string' && (
                            <div
                                key={username}
                                className="cp-listado-usernames-item"
                                onClick={() => handleUsernameSelect(username)}
                            >
                                <span className="cp-listado-usernames-text">{username}</span>
                                <button
                                    className="cp-listado-usernames-btn"
                                    title="Ver detalle"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleUsernameSelect(username);
                                    }}
                                >
                                    <Eye size={18} />
                                </button>
                            </div>
                        )
                    ))}
                </div>
            ) : (
                // Tabla de proveedores cuando está en modo "No Activos" (getInactiveProviders)
                <div className="providers-table-container">
                    <table className="providers-table">
                        <thead>
                        <tr>
                            <th>Username</th>
                            <th>Nombre</th>
                            <th>Email</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                        </thead>
                        <tbody>
                        {providers.map(p => (
                            <tr key={p.username} className={p.active ? "active-provider-row" : "inactive-provider-row"}>
                                <td>{p.username}</td>
                                <td>{p.name}</td>
                                <td>{p.email}</td>
                                <td>{p.type === 'service' ? 'Servicio' : 'Productos'}</td>
                                <td>
                                    <span className={`provider-status-badge ${p.active ? 'status-active' : 'status-inactive'}`}>
                                        {p.active ? 'Activo' : 'Inactivo'}
                                    </span>
                                </td>
                                <td className="inactive-providers-table-action-buttons">
                                    {!p.active && (
                                        <button
                                            className="inactive-providers-action-btn inactive-providers-activate-btn"
                                            title="Activar"
                                            onClick={() => handleActivateProvider(p.username)}
                                        >
                                            <Check size={18}/>
                                            <span className="provider-action-text">Activar</span>
                                        </button>
                                    )}
                                    <button
                                        className="inactive-providers-action-btn inactive-providers-view-btn"
                                        title="Ver detalle"
                                        onClick={() => {
                                            setSelectedProvider(p);
                                            getAndSetImages(p);
                                        }}
                                    >
                                        <Eye size={18}/>
                                        <span className="provider-action-text">Ver</span>
                                    </button>
                                    <button
                                        className="inactive-providers-action-btn inactive-providers-duplicate-btn"
                                        title="Duplicar proveedor"
                                        onClick={() => handleDuplicateProvider(p)}
                                    >
                                        <Copy size={18}/>
                                        <span className="provider-action-text">Duplicar</span>
                                    </button>
                                    <button
                                        className="inactive-providers-action-btn inactive-providers-delete-btn"
                                        title="Eliminar proveedor"
                                        onClick={() => handleDeleteProvider(p.username, p.type)}
                                    >
                                        <FileX size={18}/>
                                        <span className="provider-action-text">Eliminar</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Componente de detalle del proveedor */}
            {selectedProvider && (
                <ProviderData
                    provider={selectedProvider}
                    images={images}
                    onClose={() => setSelectedProvider(null)}
                    onProviderAction={handleProviderAction}
                />
            )}

            {/* Modal de creación de proveedor */}
            {showCreateModal && (
                <CreateProviderModal
                    onClose={() => {
                        setShowCreateModal(false);
                        setProviderToDuplicate(null);
                    }}
                    onCreateProvider={handleCreateProvider}
                    providerToDuplicate={providerToDuplicate}
                />
            )}

            {/* Modal para copiar usernames */}
            {showCopyModal && (
                <div className="modal-overlay" onClick={() => setShowCopyModal(false)}>
                    <div className="modal-content inactive-providers-copy-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="inactive-providers-modal-header">
                            <h3>📋 Usernames de Proveedores</h3>
                            <button
                                className="inactive-providers-modal-close-btn"
                                onClick={() => setShowCopyModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="inactive-providers-modal-body">
                            <div className="inactive-providers-usernames-count">
                                <span>📊</span>
                                <span>Total: {providers.filter(p => typeof p === 'string' ? p : p.username).length} proveedores</span>
                            </div>
                            <p>Los siguientes usernames serán copiados al portapapeles:</p>
                            <div className="inactive-providers-usernames-preview">
                                {providers
                                    .map(provider => typeof provider === 'string' ? provider : provider.username)
                                    .filter(Boolean)
                                    .join(', ')}
                            </div>
                            <div className="inactive-providers-modal-actions">
                                <button
                                    className="inactive-providers-btn-secondary"
                                    onClick={() => setShowCopyModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="inactive-providers-btn-primary"
                                    onClick={handleCopyUsernames}
                                >
                                    <Copy size={16} />
                                    Copiar al Portapapeles
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InactiveProvidersList;
